import { useRef } from 'react';

import type { ContentExtensionMetrics } from '@confluence/view-content-perf-metrics/entry-points/extensionAnalyticsHelpers.ts';
import { retrieveContentExtensionMetrics } from '@confluence/view-content-perf-metrics/entry-points/extensionAnalyticsHelpers.ts';

type ContentBody = Parameters<typeof retrieveContentExtensionMetrics>[0] | null | undefined;
type CustomPerfData = { contentExtensionMetrics?: ContentExtensionMetrics } | null | undefined;

export function useContentExtensionMetrics(
	contentBody: ContentBody,
	customPerfData: CustomPerfData,
): void {
	const memoRef = useRef({
		contentBody: undefined as typeof contentBody,
		contentExtensionMetrics: undefined as undefined | ContentExtensionMetrics | {},
	});

	if (
		customPerfData &&
		(memoRef.current.contentBody !== contentBody || !('contentExtensionMetrics' in customPerfData))
	) {
		// `retrieveContentExtensionsMetrics` parses the ADF string into JSON, but that's likely to be only one of multiple such parsings.
		// For example, the Atlaskit editor/renderer will very likely parse the ADF string into JSON as well.
		// For large documents (e.g. 16MB) such parsing can take noticeable time which is undesirable especially before rendering.
		// This allows an alternative to `retrieveContentExtensionsMetrics` to optionally perform the computation in a more performant way.
		// But if it doesn't, `retrieveContentExtensionsMetrics` will be the fallback.
		let propertyValue: undefined | ContentExtensionMetrics;

		Object.defineProperty(customPerfData, 'contentExtensionMetrics', {
			enumerable: true,
			get() {
				if (propertyValue) {
					return propertyValue;
				}
				memoRef.current.contentBody = contentBody;
				const contentExtensionMetrics = contentBody
					? retrieveContentExtensionMetrics(contentBody)
					: {};
				memoRef.current.contentExtensionMetrics = contentExtensionMetrics;
				return memoRef.current.contentExtensionMetrics;
			},
			set(value: typeof propertyValue) {
				propertyValue = value;
			},
		});
	}
}
